import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import logoImg from '../../assets/logo-cargoags-sm.png';
import { Button } from '../ui/button';
import { Ship, Plane, Phone, Menu, X, MessageCircle, User, Store, Ruler, Briefcase, Info, ChevronDown, ClipboardList } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [rates, setRates] = useState({ yuan_rate: 6.65 });
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await axios.get(`${API}/settings/exchange-rate`);
        setRates(response.data);
      } catch (error) {
        console.error('Error fetching rates');
      }
    };
    fetchRates();
  }, []);
  
  return (
    <header className="fixed top-0 left-0 right-0 bg-[#060a13]/95 backdrop-blur-md z-50 border-b border-blue-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-16">
          {/* Chinese Flag + Logo */}
          <div className="flex items-center gap-2">
            <div className="rounded-lg overflow-hidden flex-shrink-0">
              <svg viewBox="0 0 60 40" className="w-10 h-7 block">
                <rect width="60" height="40" fill="#DE2910" />
                <g fill="#FFDE00">
                  <polygon points="10,4 11.2,7 14.5,7 11.9,9.2 12.8,12.5 10,10.5 7.2,12.5 8.1,9.2 5.5,7 8.8,7" />
                  <polygon points="18,2 18.5,3.4 20,3.4 18.8,4.3 19.2,5.7 18,4.8 16.8,5.7 17.2,4.3 16,3.4 17.5,3.4" />
                  <polygon points="21,6 21.5,7.4 23,7.4 21.8,8.3 22.2,9.7 21,8.8 19.8,9.7 20.2,8.3 19,7.4 20.5,7.4" />
                  <polygon points="21,11 21.5,12.4 23,12.4 21.8,13.3 22.2,14.7 21,13.8 19.8,14.7 20.2,13.3 19,12.4 20.5,12.4" />
                  <polygon points="18,15 18.5,16.4 20,16.4 18.8,17.3 19.2,18.7 18,17.8 16.8,18.7 17.2,17.3 16,16.4 17.5,16.4" />
                </g>
              </svg>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <img src={logoImg} alt="CargoAgs" className="w-10 h-10 flex-shrink-0 drop-shadow-[0_0_10px_rgba(59,130,246,0.45)]" data-testid="header-logo" />
              <span className="text-xl font-bold text-white">CargoAgs</span>
            </Link>
          </div>
          
          <nav className="hidden lg:flex items-center gap-2">
            <div className="relative group">
              <a href="/#services" className="px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-cyan-500/25 hover:border-cyan-400/60 hover:shadow-[0_0_15px_-3px_rgba(6,182,212,0.4)] transition-all" data-testid="services-link">
                <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                Послуги
                <ChevronDown className="w-3 h-3 flex-shrink-0 group-hover:rotate-180 transition-transform" />
              </a>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="w-64 rounded-xl border border-blue-500/30 bg-[#0a1224]/95 backdrop-blur-xl shadow-[0_0_30px_-10px_rgba(59,130,246,0.6)] py-2 overflow-hidden">
                  <Link to="/usa" className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-colors" data-testid="usa-delivery-link">
                    <Plane className="w-3.5 h-3.5 flex-shrink-0" /> Доставка з США
                  </Link>
                  <Link to="/vykup-tovariv-z-kytayu" className="block px-4 py-2.5 text-sm text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" data-testid="nav-vykup-link">
                    🛒 Викуп товарів з 1688, Taobao
                  </Link>
                  <Link to="/oplata-postachalnykam-u-kytai" className="block px-4 py-2.5 text-sm text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" data-testid="nav-oplata-link">
                    💳 Оплата постачальникам у Китай
                  </Link>
                  <Link to="/poshuk-postachalnykiv-v-kytai" className="block px-4 py-2.5 text-sm text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" data-testid="nav-postachalnyky-link">
                    🏭 Пошук постачальників (B2B)
                  </Link>
                  <Link to="/dostavka-zbirnyh-vantazhiv" className="block px-4 py-2.5 text-sm text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" data-testid="nav-zbirni-link">
                    📦 Збірні вантажі з Китаю
                  </Link>
                  <Link to="/strakhuvannya-vantazhu" className="block px-4 py-2.5 text-sm text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors" data-testid="nav-strakhuvannia-link">
                    🛡️ Страхування вантажу
                  </Link>
                  <Link to="/pravyla-perevezen" className="block px-4 py-2.5 text-sm text-orange-300 hover:text-orange-200 hover:bg-orange-500/10 transition-colors" data-testid="nav-pravyla-link">
                    📋 Правила перевезень
                  </Link>
                  <a href="/#services" className="block px-4 py-2.5 text-sm text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors border-t border-slate-700/50 mt-1">
                    Всі послуги →
                  </a>
                </div>
              </div>
            </div>
            <a href="#tariffs" className="px-3 py-1.5 bg-amber-500/15 border border-amber-500/40 text-amber-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-amber-500/25 hover:border-amber-400/60 hover:shadow-[0_0_15px_-3px_rgba(245,158,11,0.4)] transition-all" data-testid="tariffs-link">
              <Ruler className="w-3.5 h-3.5 flex-shrink-0" />
              Тарифи
            </a>
            <a href="#about" className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/40 text-purple-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-purple-500/25 hover:border-purple-400/60 hover:shadow-[0_0_15px_-3px_rgba(168,85,247,0.4)] transition-all" data-testid="about-link">
              <Info className="w-3.5 h-3.5 flex-shrink-0" />
              Про нас
            </a>
            <a href="#contact" className="px-3 py-1.5 bg-pink-500/15 border border-pink-500/40 text-pink-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-pink-500/25 hover:border-pink-400/60 hover:shadow-[0_0_15px_-3px_rgba(236,72,153,0.4)] transition-all" data-testid="contact-link">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              Контакти
            </a>
            <Link to="/forum" className="relative px-3 py-1.5 bg-blue-500/20 border border-blue-500/40 text-blue-400 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-orange-500/20 hover:border-orange-500/50 hover:text-orange-400 hover:shadow-[0_0_15px_-3px_rgba(249,115,22,0.4)] transition-all" data-testid="forum-link">
              <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Форум
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
            </Link>
            <Link to="/shop" className="relative px-3 py-1.5 bg-orange-500/20 border border-orange-500/40 text-orange-400 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap hover:bg-orange-500/30 hover:border-orange-400/60 hover:shadow-[0_0_15px_-3px_rgba(249,115,22,0.4)] transition-all" data-testid="shop-link">
              <Store className="w-3.5 h-3.5 flex-shrink-0" />
              Магазин
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
            </Link>
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/10 border border-orange-500/20 rounded-lg whitespace-nowrap" data-testid="yuan-rate-display">
              <span className="text-orange-400 font-bold text-sm">¥</span>
              <span className="text-xs font-semibold text-orange-300">1 USD = {rates.yuan_rate} CNY</span>
            </div>
            {user ? (
              <Link to="/dashboard">
                <Button size="sm" className="text-sm h-8 bg-blue-600 hover:bg-orange-500 shadow-[0_0_15px_-3px_rgba(59,130,246,0.5)] hover:shadow-[0_0_20px_-3px_rgba(249,115,22,0.6)] transition-all" data-testid="header-dashboard-btn">
                  <User className="w-4 h-4 mr-1" />
                  Кабінет
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-sm h-8 text-slate-300 hover:text-orange-400 hover:bg-orange-500/10" data-testid="header-login-btn">Увійти</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="text-sm h-8 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-orange-500 hover:to-orange-400 shadow-[0_0_15px_-3px_rgba(59,130,246,0.5)] hover:shadow-[0_0_20px_-3px_rgba(249,115,22,0.6)] transition-all border-0" data-testid="header-register-btn">Реєстрація</Button>
                </Link>
              </>
            )}
            <div className="rounded-lg overflow-hidden flex-shrink-0">
              <svg viewBox="0 0 60 40" className="w-8 h-5 block">
                <rect width="60" height="20" fill="#005BBB" />
                <rect y="20" width="60" height="20" fill="#FFD500" />
              </svg>
            </div>
          </nav>
          
          <div className="hidden md:flex lg:hidden items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg" data-testid="yuan-rate-display-md">
              <span className="text-orange-400 font-bold text-sm">¥</span>
              <span className="text-xs font-semibold text-orange-300">1 USD = {rates.yuan_rate} CNY</span>
            </div>
            {user ? (
              <Link to="/dashboard">
                <Button size="sm" className="bg-blue-600 hover:bg-orange-500 shadow-[0_0_15px_-3px_rgba(59,130,246,0.5)] hover:shadow-[0_0_20px_-3px_rgba(249,115,22,0.6)]" data-testid="header-dashboard-btn-md">
                  <User className="w-4 h-4 mr-1" /> Кабінет
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-slate-300 hover:text-orange-400" data-testid="header-login-btn-md">Увійти</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-orange-500 hover:to-orange-400 shadow-[0_0_15px_-3px_rgba(59,130,246,0.5)] hover:shadow-[0_0_20px_-3px_rgba(249,115,22,0.6)]" data-testid="header-register-btn-md">Реєстрація</Button>
                </Link>
              </>
            )}
          </div>
          
          <button 
            className="md:hidden p-2 text-slate-300" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile neon nav bar */}
        <div className="md:hidden -mx-4 px-4 pb-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="mobile-nav-bar">
          <div className="flex items-center gap-2 w-max">
            <a href="/#services" className="px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="services-link-mobile-bar">
              <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
              Послуги
            </a>
            <a href="/#tariffs" className="px-3 py-1.5 bg-amber-500/15 border border-amber-500/40 text-amber-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="tariffs-link-mobile-bar">
              <Ruler className="w-3.5 h-3.5 flex-shrink-0" />
              Тарифи
            </a>
            <a href="/#about" className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/40 text-purple-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="about-link-mobile-bar">
              <Info className="w-3.5 h-3.5 flex-shrink-0" />
              Про нас
            </a>
            <a href="/#contact" className="px-3 py-1.5 bg-pink-500/15 border border-pink-500/40 text-pink-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="contact-link-mobile-bar">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              Контакти
            </a>
            <Link to="/forum" className="relative px-3 py-1.5 bg-blue-500/20 border border-blue-500/40 text-blue-400 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="forum-link-mobile-bar">
              <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Форум
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
            </Link>
            <Link to="/shop" className="relative px-3 py-1.5 bg-orange-500/20 border border-orange-500/40 text-orange-400 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="shop-link-mobile-bar">
              <Store className="w-3.5 h-3.5 flex-shrink-0" />
              Магазин
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
            </Link>
            <Link to="/pravyla-perevezen" className="px-3 py-1.5 bg-orange-500/15 border border-orange-500/40 text-orange-300 rounded-lg font-medium text-sm flex items-center gap-1 whitespace-nowrap flex-shrink-0" data-testid="pravyla-link-mobile-bar">
              <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
              Правила
            </Link>
            <div className="flex items-center gap-1 px-2 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg whitespace-nowrap flex-shrink-0">
              <span className="text-orange-400 font-bold text-sm">¥</span>
              <span className="text-xs font-semibold text-orange-300">1 USD = {rates.yuan_rate} CNY</span>
            </div>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-700/50">
            <div className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/20 rounded-lg mb-4">
              <span className="text-orange-400 font-bold">¥</span>
              <span className="text-sm font-semibold text-orange-300">1 USD = {rates.yuan_rate} CNY</span>
            </div>
            <nav className="flex flex-col gap-4">
              <a href="/#services" className="text-slate-300 hover:text-blue-400 py-2">Послуги</a>
              <Link to="/usa" className="text-red-300 hover:text-red-200 py-1 pl-4 text-sm" data-testid="usa-delivery-link-mobile">✈️ Доставка з США</Link>
              <Link to="/vykup-tovariv-z-kytayu" className="text-slate-400 hover:text-cyan-300 py-1 pl-4 text-sm">🛒 Викуп товарів з 1688, Taobao</Link>
              <Link to="/oplata-postachalnykam-u-kytai" className="text-slate-400 hover:text-cyan-300 py-1 pl-4 text-sm">💳 Оплата постачальникам у Китай</Link>
              <Link to="/poshuk-postachalnykiv-v-kytai" className="text-slate-400 hover:text-cyan-300 py-1 pl-4 text-sm">🏭 Пошук постачальників (B2B)</Link>
              <Link to="/dostavka-zbirnyh-vantazhiv" className="text-slate-400 hover:text-cyan-300 py-1 pl-4 text-sm">📦 Збірні вантажі з Китаю</Link>
              <Link to="/strakhuvannya-vantazhu" className="text-slate-400 hover:text-cyan-300 py-1 pl-4 text-sm">🛡️ Страхування вантажу</Link>
              <Link to="/pravyla-perevezen" className="text-orange-300 hover:text-orange-200 py-1 pl-4 text-sm" data-testid="nav-pravyla-link-mobile">📋 Правила перевезень</Link>
              <a href="#tariffs" className="text-slate-300 hover:text-blue-400 py-2">Тарифи</a>
              <a href="#about" className="text-slate-300 hover:text-blue-400 py-2">Про нас</a>
              <a href="#contact" className="text-slate-300 hover:text-blue-400 py-2">Контакти</a>
              <Link to="/shop" className="text-orange-400 hover:text-orange-300 font-semibold py-2 flex items-center gap-2" data-testid="shop-link-mobile">
                <Store className="w-4 h-4" /> Магазин
              </Link>
              <div className="flex gap-4 pt-4 border-t border-slate-700/50">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-white hover:border-blue-500">Увійти</Button>
                </Link>
                <Link to="/register" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-500">Реєстрація</Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
